<!--
	Attributes for the selected map feature, with a collapse toggle and prev/next navigation when
	several are selected. Apps either configure `attributes.columns` (default icon) or wrap it with their own icon.
-->
<script module lang="ts">
	import type { MapGeoJSONFeature } from 'maplibre-gl';
	import { DEFAULT_FEATURE_COLOR } from '#lib/vizPalette.js';

	const COLOR_PROP: Record<string, string> = {
		line: 'line-color',
		circle: 'circle-color',
		symbol: 'text-color'
	};

	/** CSS colour of a feature layer's plain line/circle/symbol paint colour, else the default. */
	export function layerPatchColor(layer: MapGeoJSONFeature['layer']): string {
		const color = (layer.paint as Record<string, unknown> | undefined)?.[COLOR_PROP[layer.type]];
		// Expression-driven paint (e.g. the MIRE explorer's `step`) isn't a plain colour.
		if (typeof color !== 'object' || color === null || !('r' in color))
			return DEFAULT_FEATURE_COLOR;
		const { r, g, b } = color as Record<string, number>;
		return `rgb(${r * 255}, ${g * 255}, ${b * 255})`;
	}
</script>

<script lang="ts">
	import type { Snippet } from 'svelte';
	import { featureSelectionStore } from '#lib/stores/featureSelectionStore.svelte.js';
	import { mapInstanceStore } from '#lib/stores/mapInstanceStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { fitFeatureBounds } from '#lib/components/map/layers.js';
	import { getPanelWidth } from '#lib/panelWidth.js';
	import { parseGeometryLocation, formatPointGeometry } from '#lib/featureGeometry.js';
	import { getStreetViewPosition } from '#lib/streetView.js';
	import type { AttributesAlertProps, AttributesLayoutHelper } from './Attributes.types';
	import Attributes from './Attributes.svelte';
	import CollapsiblePanel from './CollapsiblePanel.svelte';
	import InfoButton from './InfoButton.svelte';
	import IconButton from './IconButton.svelte';
	import LegendPatch from './LegendPatch.svelte';
	import IconChevronRight from '#lib/assets/chevron-right.svg?raw';
	import IconChevronLeft from '#lib/assets/chevron-left.svg?raw';

	type Attrs = Record<string, string | number>;

	interface Props {
		/** Attribute layouts keyed by system layer id. */
		columns: Record<string, AttributesLayoutHelper>;
		/** Title icon; defaults to a round patch in the feature's line colour. */
		icon?: Snippet<[MapGeoJSONFeature, string]>;
		/** Adds derived values to the copied properties; defaults to the feature's `position`. */
		enrich?: (attrs: Attrs, feature: MapGeoJSONFeature, systemId: string) => void;
		alerts?: (systemId: string, attrs: Attrs) => (AttributesAlertProps | undefined)[];
		/** Rendered below the attributes. */
		extra?: Snippet<[MapGeoJSONFeature, string]>;
		/** Shows the system layer id beside the panel title. */
		showLayerName?: boolean;
	}

	const {
		columns,
		icon,
		enrich = (attrs, feature) => {
			const position = parseGeometryLocation(feature.geometry);
			if (position) attrs.position = formatPointGeometry(position);
		},
		alerts,
		extra,
		showLayerName = false
	}: Props = $props();

	const feature = $derived(featureSelectionStore.current);
	const systemId = $derived(featureSelectionStore.currentSystemId);
	const attributesCols = $derived(systemId ? columns[systemId] : undefined);

	const enrichedAttrs = $derived.by((): Attrs | undefined => {
		if (!feature || !systemId || !attributesCols) return undefined;
		const attrs = { ...feature.properties } as Attrs;
		enrich(attrs, feature, systemId);
		return attrs;
	});

	const streetViewCoords = $derived(
		feature ? (getStreetViewPosition(feature) ?? undefined) : undefined
	);

	function goToFeature() {
		if (feature && mapInstanceStore.map)
			fitFeatureBounds(mapInstanceStore.map, feature, getPanelWidth());
	}
</script>

{#if feature && systemId && attributesCols && enrichedAttrs}
	{@const selectedCount = featureSelectionStore.selectedFeatures.length}
	{@const currentIndex = featureSelectionStore.currentSelectedFeatureIndex}

	{#snippet layerName()}
		<span class="layerName">{systemId}</span>
	{/snippet}

	{#snippet titleIcon()}
		{#if icon}
			{@render icon(feature, systemId)}
		{:else}
			<LegendPatch
				color={feature.layer.type === 'line'
					? layerPatchColor(feature.layer)
					: DEFAULT_FEATURE_COLOR}
				shape="round" />
		{/if}
	{/snippet}

	{#snippet additionalActions()}
		{@const text = geodataStore.layerAbstract(systemId)}
		{#if text}
			<InfoButton content={text} position="left" maxWidth={400} />
		{/if}
	{/snippet}

	<CollapsiblePanel title="Attributes" titleExtra={showLayerName ? layerName : undefined}>
		<Attributes
			icon={titleIcon}
			{additionalActions}
			title={attributesCols.title}
			subheaders={attributesCols.subheaders}
			alerts={alerts?.(systemId, enrichedAttrs)}
			body={attributesCols.body}
			table={attributesCols.table}
			addenda={attributesCols.addenda}
			zoomTo={goToFeature}
			streetView={streetViewCoords}
			data={enrichedAttrs} />

		{@render extra?.(feature, systemId)}

		{#if selectedCount > 1}
			<div class="attributesControls">
				<p aria-live="polite">{currentIndex + 1} of {selectedCount}</p>
				<div class="navSection">
					<IconButton
						variant="quiet"
						size="xs"
						aria-label="Previous feature"
						onclick={() => featureSelectionStore.decrementSelectedFeature()}>
						{@html IconChevronLeft}
					</IconButton>
					<IconButton
						variant="quiet"
						size="xs"
						aria-label="Next feature"
						onclick={() => featureSelectionStore.incrementSelectedFeature()}>
						{@html IconChevronRight}
					</IconButton>
				</div>
			</div>
		{/if}
	</CollapsiblePanel>
{/if}

<style>
	.layerName {
		color: var(--text-muted);
		font-size: var(--font-size-sm);
	}

	.attributesControls {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: var(--space-4);
		background-color: var(--surface-alt);
		padding: 0 var(--space-6) var(--space-5);
	}

	.navSection {
		display: flex;
		gap: var(--space-2);
	}
</style>
